import { route } from "./routes.js";
import { z } from "zod";
//#region src/auth.ts
/**
* `/v1/auth/me` — the one `/v1/auth` route the admin app calls: who the caller is.
*
* Producer: `AuthController.getMe` and `AuthService.getMe` in `cloud/dp/core-api/src/auth/`.
* Consumer: the admin app's `AdminClient`, which reads the caller's user id so a page can disable
* a control whose target is the caller.
*
* The route sits outside `/v1/admin`, so this file writes the path literally. It carries no
* permission key: any valid data-plane bearer token reads its own profile.
*
* Direction rule (R-28): the response schema is `z.object`, so a member core-api adds is stripped
* rather than refused. The route takes no body and no query, so this file declares no request
* schema.
*/
/**
* `GET /v1/auth/me` — the calling user's profile. Status 200. No permission key; the bearer token
* alone authorizes it.
*
* Errors: 401 when the request carries no valid data-plane bearer token; 404 `User not found` when
* the token's subject names no user visible in the token's organization.
*/
const GET_ME_ROUTE = route("GET", "/v1/auth/me");
/**
* The body of `GET /v1/auth/me`, narrowed to the three members the admin app reads.
*
* `id` is the caller's `auth.users` uuid, the value every admin user row carries as `id`, so a page
* compares it to a row's `id` or a session's `userId` to find the caller. `email` is the login
* address. `fullName` is the user's `displayName`.
*
* The producer also sends `emailVerified`, `googleAvatarUrl`, `defaultOrganizationId`,
* `createdAt`, `updatedAt` and `branding`. `z.object` strips them, so this contract does not pin
* their shapes and a change to any of them cannot break a page that parses this schema.
*
* Owner: `UserProfile` returned by `AuthService.getMe`, plus the `branding` member
* `AuthController.getMe` adds.
*/
const meResponseSchema = z.object({
	id: z.uuid(),
	email: z.string(),
	fullName: z.string()
});
/** Every route this file names, for `route-census.ts`. */
const authRoutes = Object.freeze([GET_ME_ROUTE]);
//#endregion
export { GET_ME_ROUTE, authRoutes, meResponseSchema };

//# sourceMappingURL=auth.js.map